import { CommonModule } from '@angular/common';
import { Component, NgModule, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Roles } from '@app/common/roles';
import { Utilities } from '@app/common/utilities';
import { CommentoModule } from '@app/components/commento/commento.component';
import { ChannelControlModule } from '@app/components/form-controls/channel-control/channel-control.component';
import { GalleryModule } from '@app/components/gallery/gallery.component';
import { Page, PageType } from '@app/models/page';
import { PeoplePickerUser } from '@app/models/people-picker-user';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { ChannelService } from '@app/services/channel.service';
import { LanguageTermService } from '@app/services/language-term.service';
import { PagesService } from '@app/services/pages.service';
import { SecurityService } from '@app/services/security.service';
import { UserService } from '@app/services/user.service';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PageComponent implements OnInit {
  page$: Observable<Page>;
  showEditMode$: Observable<boolean>;
  selectedChannels$: Observable<Array<SimplifiedTerm>>;
  language$: Observable<string>;
  modifiedByUser$: Observable<PeoplePickerUser>;
  ownerUser$: Observable<PeoplePickerUser>;
  galleryImages$: Observable<Array<string>>;

  constructor(private activatedRoute: ActivatedRoute,
              private securityService: SecurityService,
              private channelService: ChannelService,
              private languageTermService: LanguageTermService,
              private userService: UserService,
              private pagesService: PagesService) { }

  ngOnInit() {
    this.page$ = this.activatedRoute.data
      .pipe(
        filter(data => data.page),
        map((data: any) => {
          return new Page(data.page);
        })
      );
    this.modifiedByUser$ = this.page$.pipe(
      switchMap(page => (page.ModifiedByUserUpnId ? this.userService.getUserByUpn(page.ModifiedByUserUpnId) : of(null))),
      map(user => {
        // map to PeoplePickerUser because we don't need all attributes
        return user ? {
          GraphId: user.id,
          Name: user.displayName,
          // for external users, use email if it exists to prevent showing #EXT# in adress
          UPN: user.userPrincipalName.match('EXT') && user.mail ? user.mail : user.userPrincipalName,
        } as PeoplePickerUser : null;
      })
    );
    this.ownerUser$ = this.page$.pipe(
      switchMap(page => (page.OwnerUserUpnId ? this.userService.getUserByUpn(page.OwnerUserUpnId) : of(null))),
      map(user => {
        // map to PeoplePickerUser because we don't need all attributes
        return user ? {
          GraphId: user.id,
          Name: user.displayName,
          // for external users, use email if it exists to prevent showing #EXT# in adress
          UPN: user.userPrincipalName.match('EXT') && user.mail ? user.mail : user.userPrincipalName,
        } as PeoplePickerUser : null;
      })
    );

    this.selectedChannels$ = combineLatest([this.page$, this.channelService.getChannels()])
      .pipe(
        map(([page, fetchedChannels]) => {
          let selectedChannels = new Array<SimplifiedTerm>();
          if (page.ChannelTermPages && page.ChannelTermPages.length > 0) {
            selectedChannels = page.ChannelTermPages.map(ct => {
              const selectedChannel = fetchedChannels.findRecursive(fc => fc.Guid === ct.ChannelTermId, 'Terms');
              if (selectedChannel) {
                return selectedChannel;
              }
            });
          }
          return selectedChannels;
        })
      );

    this.language$ = combineLatest([this.page$, this.languageTermService.getLanguages()])
      .pipe(
        map(([page, languages]) => {
          if (!page.LanguageTermId || languages.length === 0) { return null; }
          const matchingLanguage = languages.find(l => l.Guid.toLowerCase() === page.LanguageTermId.toLowerCase());
          if (matchingLanguage && matchingLanguage.Name) {
            return matchingLanguage.Name;
          }
        })
      );

    this.showEditMode$ = this.securityService.hasUserAnyAppRole([Roles.Admin, Roles.Editor]);
    this.galleryImages$ = this.pagesService.getImages();
  }

  getPreferredNameLetters(userName: string) {
    return Utilities.generateInitials(userName);
  }

  getCommentoPageId(page: Page) {
    return `workplace/pages/${(page.PageType === PageType.News ? 'news' : 'content')}/${page.Id}`;
  }
}

@NgModule({
  declarations: [
    PageComponent
  ],
  exports: [
    PageComponent
  ],
  imports: [
    CommonModule,
    LinkModule,
    RouterModule,
    SafeModule,
    ChannelControlModule,
    MatButtonModule,
    MatChipsModule,
    GalleryModule,
    CommentoModule
  ]
})
export class PageModule { }
